import { useState, useEffect } from "react";
import {
	Box,
	Typography,
	TextField,
	Autocomplete,
	Button,
	CircularProgress,
	Grid,
	IconButton,
} from "@mui/material";
import { ArrowBack, Delete } from "@mui/icons-material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import MDButton from "../../components/MDButton";
import apiService from "../../services/api-service";

const WORK_PACKAGES = [
	{ value: "FINITURE", label: "Finiture" },
	{ value: "STRUTTURE", label: "Strutture" },
	{ value: "IMPIANTI", label: "Impianti" },
	{ value: "IMPERMEABILIZZAZIONE", label: "Impermeabilizzazione" },
	{ value: "COPERTURA", label: "Copertura" },
	{ value: "SERRAMENTI", label: "Serramenti" },
	{ value: "PAVIMENTAZIONE", label: "Pavimentazione" },
	{ value: "ALTRO", label: "Altro" },
];

const SnagItemAdd = () => {
	const { floorPlanId } = useParams();
	const location = useLocation();
	const navigate = useNavigate();

	const [xPercent] = useState(() => location.state?.x ?? 0);
	const [yPercent] = useState(() => location.state?.y ?? 0);

	const [floorPlan, setFloorPlan] = useState(null);
	const [loading, setLoading] = useState(true);
	const [imageLoaded, setImageLoaded] = useState(false);
	const [saving, setSaving] = useState(false);
	const [formData, setFormData] = useState({
		work_package: null,
		description: "",
		images: [],
	});

	useEffect(() => {
		fetchFloorPlan();
	}, [floorPlanId]);

	const fetchFloorPlan = async () => {
		try {
			const response = await apiService.get(`/snaglist/floor-plans/${floorPlanId}/`, {}, {});
			setFloorPlan(response);
		} catch (error) {
			toast.error("Errore caricamento planimetria");
		} finally {
			setLoading(false);
		}
	};

	const handleImageUpload = (e) => {
		const files = Array.from(e.target.files);
		files.forEach((file) => {
			const reader = new FileReader();
			reader.onload = () => {
				setFormData((prev) => ({
					...prev,
					images: [
						...prev.images,
						{
							name: `snaglist/snag-images/${Date.now()}_${file.name}`,
							content: reader.result,
							type: file.type,
							size: file.size,
						},
					],
				}));
			};
			reader.readAsDataURL(file);
		});
	};

	const handleRemoveImage = (index) => {
		setFormData((prev) => ({
			...prev,
			images: prev.images.filter((_, i) => i !== index),
		}));
	};

	const handleSave = async () => {
		if (!formData.work_package || !formData.description) {
			toast.error("Compila tutti i campi obbligatori");
			return;
		}

		setSaving(true);
		try {
			await apiService.post(
				"/snaglist/snag-items/",
				{},
				{
					floor_plan_id: parseInt(floorPlanId),
					x_percent: xPercent,
					y_percent: yPercent,
					work_package: formData.work_package,
					description: formData.description,
					images: formData.images,
				}
			);
			toast.success("Snag creato con successo");
			navigate(`/snag-list/floor-plan/${floorPlan?.area_id || 0}/${floorPlanId}`);
		} catch (error) {
			toast.error("Errore creazione snag: " + error.message);
		} finally {
			setSaving(false);
		}
	};

	if (loading) {
		return (
			<DashboardLayout>
				<DashboardNavbar />
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
					<CircularProgress />
				</Box>
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<Box p={3}>
				<Box display="flex" alignItems="center" gap={2} mb={3}>
					<MDButton
						variant="outlined"
						color="info"
						startIcon={<ArrowBack />}
						onClick={() => navigate(`/snag-list`)}
					>
						Torna alla lista
					</MDButton>
					<Typography variant="h4" color="primary">
						Nuovo Snag
					</Typography>
				</Box>

				<Grid container spacing={3}>
					{/* Mini floor plan preview */}
					<Grid item xs={12} md={5}>
						<Typography variant="h6" mb={1}>
							Posizione selezionata
						</Typography>
						{floorPlan?.image_url && (
							<Box
								sx={{
									position: "relative",
									width: "100%",
									borderRadius: 2,
									overflow: "hidden",
									boxShadow: 2,
								}}
							>
								<img
									src={floorPlan.image_url}
									alt={floorPlan.name}
									style={{ width: "100%", display: "block" }}
									onLoad={() => setImageLoaded(true)}
								/>
								{/* Marker at selected position - show only after image loads */}
								{imageLoaded && (
									<Box
										sx={{
											position: "absolute",
											left: `${xPercent}%`,
											top: `${yPercent}%`,
											transform: "translate(-50%, -50%)",
											width: 20,
											height: 20,
											borderRadius: "50%",
											bgcolor: "#ff9800",
											border: "3px solid white",
											boxShadow: "0 2px 6px rgba(0,0,0,0.4)",
										}}
									/>
								)}
							</Box>
						)}
						<Typography variant="caption" color="textSecondary" mt={1}>
							X: {xPercent.toFixed(1)}% - Y: {yPercent.toFixed(1)}%
						</Typography>
					</Grid>

					{/* Form */}
					<Grid item xs={12} md={7}>
						<Box display="flex" flexDirection="column" gap={2}>
							<Autocomplete
								options={WORK_PACKAGES}
								getOptionLabel={(option) => option.label}
								value={WORK_PACKAGES.find((wp) => wp.value === formData.work_package) || null}
								onChange={(event, newValue) =>
									setFormData((prev) => ({ ...prev, work_package: newValue?.value || null }))
								}
								renderInput={(params) => (
									<TextField {...params} label="Pacchetto di Lavoro *" />
								)}
							/>

							<TextField
								label="Descrizione *"
								multiline
								rows={4}
								value={formData.description}
								onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
								fullWidth
							/>

							{/* Image upload */}
							<Box>
								<Typography variant="subtitle2" mb={1}>
									Immagini
								</Typography>
								<Button variant="outlined" component="label">
									Carica immagini
									<input type="file" hidden accept="image/*" multiple onChange={handleImageUpload} />
								</Button>

								{formData.images.length > 0 && (
									<Box display="flex" gap={1} flexWrap="wrap" mt={1}>
										{formData.images.map((img, index) => (
											<Box
												key={index}
												sx={{
													position: "relative",
													width: 80,
													height: 80,
													borderRadius: 1,
													overflow: "hidden",
													border: "1px solid #ddd",
												}}
											>
												<img
													src={img.content}
													alt={`img-${index}`}
													style={{ width: "100%", height: "100%", objectFit: "cover" }}
												/>
												<IconButton
													size="small"
													onClick={() => handleRemoveImage(index)}
													sx={{
														position: "absolute",
														top: 0,
														right: 0,
														bgcolor: "rgba(255,255,255,0.8)",
														p: 0.25,
													}}
												>
													<Delete fontSize="small" color="error" />
												</IconButton>
											</Box>
										))}
									</Box>
								)}
							</Box>

							<Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
								<Button variant="outlined" onClick={() => navigate(-1)}>
									Annulla
								</Button>
								<MDButton
									variant="contained"
									color="success"
									onClick={handleSave}
									disabled={saving}
								>
									{saving ? <CircularProgress size={20} /> : "Salva Snag"}
								</MDButton>
							</Box>
						</Box>
					</Grid>
				</Grid>
			</Box>
		</DashboardLayout>
	);
};

export default SnagItemAdd;
